// THE REASONING SUMMARIES, AS THE CHAT READS THEM (owner defect report, 11 Aug 2026).
//
// Two presentation defects, one record. The durable `thinking_summary` rows are correct and stay exactly as
// recorded — nothing here edits, drops, or reorders a byte of them (P-SELF). This module only decides how
// they READ once the answer has landed.
//
//   (1) VOLUME. The new model summarises its thinking several times per run, so a finished answer arrived
//       under a stack of long italic paragraphs — "volume without value once the answer exists". The chat
//       needs ONE quiet row per run of thinking, opened on demand, not N loud ones — and still in the order
//       the work actually happened, which is why the fold stops at the first row that is not a thought.
//
//   (2) LITERAL ASTERISKS. The summaries carry markdown emphasis and the surface printed the markers
//       verbatim: "**Formulating performance update** I'm thinking about…". The leading bold run is the
//       step's own title, so it should read as one.
//
// Both fixes are RENDER-SIDE on purpose. The producer's one-line trim (the substrate adapter) writes the
// label the evidence keeps; changing it would change the record rather than the reading of it.

import type { ChatActivityItem } from './projections.ts';

/** One run of a summary line: its text, and whether the label marked it as emphasis. */
export interface ThoughtSegment {
  readonly text: string;
  readonly strong: boolean;
}

/** Asterisks left stranded at a segment edge — an odd `***`, or a marker the producer's 160-character cap
 *  bisected. They are markup, not words, so they never reach the screen either way. */
const STRAY_ASTERISKS = /^\*+|\*+$/g;

/**
 * Split one summary label into its plain and emphasised runs.
 *
 * The producer caps a label at 160 characters, so a `**bold**` marker can be cut in half mid-token and the
 * text arrives with an OPENER and no closer. Splitting on the marker and alternating handles that for free:
 * the unterminated tail keeps the emphasis its opener asked for, which is what the writer meant, and no
 * asterisk survives to be printed either way. An empty run is dropped so a leading `**` adds no empty node.
 */
export function thoughtSegments(label: string): readonly ThoughtSegment[] {
  const segments: ThoughtSegment[] = [];
  label.split('**').forEach((piece, index) => {
    const text = piece.replace(STRAY_ASTERISKS, '');
    if (text === '') return;
    segments.push({ text, strong: index % 2 === 1 });
  });
  return segments;
}

/** One rendered entry: an ordinary trace row, or the folded disclosure standing in for a run of summaries. */
export type FoldedTraceEntry<T> =
  | { readonly type: 'row'; readonly row: T }
  | { readonly type: 'summaries'; readonly rows: readonly ChatActivityItem[] };

/**
 * Fold each ADJACENT run of reasoning summaries into a single entry standing exactly where that run stood.
 *
 * WHY ADJACENT RATHER THAN THE WHOLE TURN. A folded group renders at ONE point in the list, so everything
 * inside it is read as having happened at that point. That is only true while the run is unbroken. Let it
 * swallow the tool call recorded between two thoughts and the collapse drags the later thoughts back above
 * work that in fact came first — the trace would show "thought, thought, thought, then read the note" for a
 * run that thought, read the note, and thought again. The harness deliberately flushes a thinking block the
 * moment other work interrupts it, "so the trace keeps the real think → act → think order"
 * (opencode-harness-reasoning.ts); throwing that away at the last inch would break the trace's standing
 * invariant that rows read in the order the server published them (BUILD-DEC-098).
 *
 * So ANY row that is not a summary — a tool call, a memory read, an answer paragraph — closes the open run
 * and renders in its own place. The stack the owner reported is still one quiet row: consecutive summaries
 * are exactly what a run deposits around its answer, and thinking split by real work reads as the two
 * stretches it was, each collapsed, each where it happened.
 *
 * Nothing is discarded: every folded summary is carried on the entry, in order, for the disclosure to show.
 */
export function foldTraceSummaries<T>(
  rows: readonly T[],
  summaryOf: (row: T) => ChatActivityItem | null,
): readonly FoldedTraceEntry<T>[] {
  const out: FoldedTraceEntry<T>[] = [];
  let run: ChatActivityItem[] = [];
  // The open run is emitted before the row that ends it, so entries leave here in publication order.
  const closeRun = (): void => {
    if (run.length === 0) return;
    out.push({ type: 'summaries', rows: run });
    run = [];
  };
  rows.forEach((row) => {
    const summary = summaryOf(row);
    if (summary !== null) {
      run.push(summary);
      return;
    }
    closeRun();
    out.push({ type: 'row', row });
  });
  closeRun();
  return out;
}
